import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetSurveyPublic, useSubmitSurvey } from '../../features/tournament/hooks/surveyHooks';
import { formatDateLiteral } from '@iziping/common';
import type { SurveyAnswer } from '@iziping/common';
import NotFound from '../../shared/pages/NotFound';
import styles from './Survey.module.css';

const StarRating: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => {
    const [hovered, setHovered] = useState(0);
    return (
        <div className={styles.stars}>
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    className={`${styles.star} ${star <= (hovered || value) ? styles.starActive : ''}`}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(0)}
                    onClick={() => onChange(star)}
                    aria-label={`${star}/5`}
                >
                    ★
                </button>
            ))}
        </div>
    );
};

const SurveyPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const { t } = useTranslation();
    const { data: survey, isLoading, isError } = useGetSurveyPublic(token ?? '');
    const submit = useSubmitSurvey(token ?? '');

    const [answers, setAnswers] = useState<Record<number, SurveyAnswer>>({});
    const [submitted, setSubmitted] = useState(false);
    const [attempted, setAttempted] = useState(false);

    const setRating = (questionId: number, ratingValue: number) => {
        setAnswers((prev) => ({ ...prev, [questionId]: { questionId, ratingValue } }));
    };

    const setText = (questionId: number, textValue: string) => {
        setAnswers((prev) => ({ ...prev, [questionId]: { questionId, textValue } }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const unansweredRating = survey?.questions.find(
            (q) => q.type === 'RATING' && !((answers[q.id!]?.ratingValue ?? 0) >= 1)
        );
        if (unansweredRating) {
            setAttempted(true);
            document.getElementById(`question-${unansweredRating.id}`)?.scrollIntoView({ behavior: 'smooth' });
            return;
        }
        await submit.mutateAsync({ answers: Object.values(answers) });
        setSubmitted(true);
    };

    if (isLoading) return <div className={styles.center}>{t('common.loading')}</div>;
    if (isError || !survey) return <NotFound context="survey" />;

    if (submitted || survey.alreadyResponded) {
        return (
            <div className={styles.page}>
                <div className={styles.card}>
                    {survey.logoUrl && <img src={survey.logoUrl} alt="logo" className={styles.logo} />}
                    <h2 className={styles.tournamentName}>{survey.tournamentName}</h2>
                    <div className={styles.thankYou}>
                        <span className={styles.thankYouIcon}>✓</span>
                        <p>{t('survey.public.already_responded')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.card}>
                {survey.logoUrl && <img src={survey.logoUrl} alt="logo" className={styles.logo} />}
                <h2 className={styles.tournamentName}>{survey.tournamentName}</h2>
                <p className={styles.meta}>
                    {formatDateLiteral(survey.tournamentDate)}
                    {survey.tournamentLocation && ` · ${survey.tournamentLocation}`}
                </p>
                <p className={styles.intro}>{t('survey.public.intro')}</p>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {survey.questions.map((q, i) => {
                        const missing = attempted && q.type === 'RATING' && !((answers[q.id!]?.ratingValue ?? 0) >= 1);
                        return (
                            <div
                                key={q.id}
                                id={`question-${q.id}`}
                                className={`${styles.questionBlock} ${missing ? styles.questionBlockError : ''}`}
                            >
                                <p className={styles.questionLabel}>
                                    <span className={styles.questionIndex}>{i + 1}</span>
                                    {q.label}
                                    {q.type === 'RATING' && (
                                        <span className={styles.ratingHint}>{t('survey.public.rating.hint')}</span>
                                    )}
                                    {missing && (
                                        <span className={styles.requiredHint}>
                                            {t('survey.public.rating.required')}
                                        </span>
                                    )}
                                </p>
                                {q.type === 'RATING' ? (
                                    <StarRating
                                        value={answers[q.id!]?.ratingValue ?? 0}
                                        onChange={(v) => setRating(q.id!, v)}
                                    />
                                ) : (
                                    <textarea
                                        className={styles.textarea}
                                        rows={3}
                                        placeholder={t('survey.public.text.placeholder')}
                                        value={answers[q.id!]?.textValue ?? ''}
                                        onChange={(e) => setText(q.id!, e.target.value)}
                                    />
                                )}
                            </div>
                        );
                    })}

                    <button type="submit" className={styles.submitBtn} disabled={submit.isPending}>
                        {submit.isPending ? '...' : t('survey.public.submit')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default SurveyPage;
